import type {UseDisclosureProps} from '@chakra-ui/react';
import {Collapse, useDisclosure} from '@chakra-ui/react';
import type {PropsOf} from '@chakra-ui/system';
import {forwardRef} from '@chakra-ui/system';
import {ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import {dataAttr} from '@utils';
import type {FC} from 'react';
import {Children, cloneElement, isValidElement, useMemo} from 'react';

import {NavMenuItem} from './Item';
import {useStyles} from './context';

export type Props = {
  isOpen?: boolean;
  isActive?: boolean;
  key: string;
} & PropsOf<typeof NavMenuItem> &
  UseDisclosureProps;

export const NavMenuGroup: FC<Props> = forwardRef(
  (
    {isOpen: isOpenProp, onClose: onCloseProp, onOpen: onOpenProp, defaultIsOpen, id, isActive, children, ...props},
    ref,
  ) => {
    const styles = useStyles();
    const {isOpen, getButtonProps} = useDisclosure({
      defaultIsOpen,
      onOpen: onOpenProp,
      onClose: onCloseProp,
      id,
      isOpen: isOpenProp,
    });

    const memoizedChildren = useMemo(
      () => Children.map(children, child => cloneElement(child, {level: 1})),
      [children],
    );

    const descendantIsActive = useMemo(() => {
      return Children.toArray(children).some(child => {
        return isValidElement(child) && (child.props as any).isActive;
      });
    }, [children]);

    return (
      <>
        <NavMenuItem
          role={`group`}
          {...props}
          level={0}
          isCollapsible
          isExpended={isOpen}
          isActive={isActive}
          data-active-descendant={dataAttr(descendantIsActive)}
          aria-activedescendant={descendantIsActive ? props.id : undefined}
          {...getButtonProps()}>
          {props.label}
        </NavMenuItem>
        <Collapse
          in={isOpen}
          animateOpacity
          data-active={dataAttr(isActive)}
          style={{display: `flex`, alignSelf: `stretch`, flexDirection: `column`}}
          transition={{enter: [0.4, 0, 0.2, 1], exit: [0, 0, 0.2, 1]}}>
          {/* // display flex in collapse doesnt play nicely */}
          <ChakraDiv
            __css={styles[`menu-group`]}
            data-active={dataAttr(isActive)}
            data-open={dataAttr(isOpen)}
            role={`group`}
            ref={ref}>
            {memoizedChildren}
          </ChakraDiv>
        </Collapse>
      </>
    );
  },
);

export type {Props as NavGroupProps};
